import React, { useState } from 'react';
import { Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';

export function DateRangePicker({ onDateChange, startDate, endDate }) {
  const [localStartDate, setLocalStartDate] = useState(startDate || '');
  const [localEndDate, setLocalEndDate] = useState(endDate || '');

  const handleApply = () => {
    if (localStartDate && localEndDate) {
      onDateChange(localStartDate, localEndDate);
    }
  };

  const handleReset = () => {
    setLocalStartDate('');
    setLocalEndDate('');
    onDateChange('', '');
  };

  const getPresetRange = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    
    const startStr = format(start, 'yyyy-MM-dd');
    const endStr = format(end, 'yyyy-MM-dd');
    
    setLocalStartDate(startStr);
    setLocalEndDate(endStr);
    onDateChange(startStr, endStr);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full sm:w-auto gap-2">
          <Calendar className="h-4 w-4" />
          <span className="hidden sm:inline">
            {startDate && endDate
              ? `${format(new Date(startDate), 'dd MMM', { locale: id })} - ${format(new Date(endDate), 'dd MMM yyyy', { locale: id })}`
              : 'Pilih Rentang Waktu'}
          </span>
          <span className="sm:hidden">Filter Tanggal</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-medium text-sm">Filter Rentang Waktu</h4>
            <p className="text-xs text-muted-foreground">
              Pilih periode untuk melihat aktivitas
            </p>
          </div>

          {/* Quick Presets */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Cepat Pilih:</Label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => getPresetRange(7)}
                className="text-xs"
              >
                7 Hari Terakhir
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => getPresetRange(30)}
                className="text-xs"
              >
                30 Hari Terakhir
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => getPresetRange(90)}
                className="text-xs"
              >
                90 Hari Terakhir
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const start = new Date();
                  start.setDate(1); // First day of current month
                  setLocalStartDate(format(start, 'yyyy-MM-dd'));
                  setLocalEndDate(format(new Date(), 'yyyy-MM-dd'));
                  onDateChange(format(start, 'yyyy-MM-dd'), format(new Date(), 'yyyy-MM-dd'));
                }}
                className="text-xs"
              >
                Bulan Ini
              </Button>
            </div>
          </div>

          {/* Custom Date Range */}
          <div className="space-y-3 pt-2 border-t">
            <div className="space-y-2">
              <Label htmlFor="start-date" className="text-sm">
                Tanggal Mulai
              </Label>
              <Input
                id="start-date"
                type="date"
                value={localStartDate}
                onChange={(e) => setLocalStartDate(e.target.value)}
                max={localEndDate || format(new Date(), 'yyyy-MM-dd')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="end-date" className="text-sm">
                Tanggal Akhir
              </Label>
              <Input
                id="end-date"
                type="date"
                value={localEndDate}
                onChange={(e) => setLocalEndDate(e.target.value)}
                min={localStartDate}
                max={format(new Date(), 'yyyy-MM-dd')}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              className="flex-1"
            >
              Reset
            </Button>
            <Button
              size="sm"
              onClick={handleApply}
              className="flex-1"
              disabled={!localStartDate || !localEndDate}
            >
              Terapkan
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
